import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';

import Store, { defaultBuilder } from 'store';
import Session from 'store/session';

import OverlayRoot, { OverlayContext } from 'ui/utils/OverlayManager';
import Overlay, { ErrorPopup } from 'ui/utils/Overlay';
import { DndProviderWrapper } from 'ui/dragdrop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faEye } from '@fortawesome/free-solid-svg-icons';
import EditingContext from 'store/editingContext';
import { ImportTracker, useImportId } from 'components/importTracker';
import Tooltip from 'ui/tooltip';
import BuilderPaperdoll from './BuilderPaperdoll';

import './builder.scss';
import ClassSelect from 'components/ClassSelect';
import BuilderStats from './BuilderStats';
import BuilderSettings from './BuilderSettings';
import { ProfileUpdateButton } from 'components/import/Profiles';
import ProfileNewButton from 'components/ProfileNewButton';
import AccountDialog from 'components/account/AccountDialog';
import BuilderEngravings from './BuilderEngravings';
import BuilderSkills from './BuilderSkills';
import BuilderCards from './BuilderCards';
import Select from 'ui/select';
import BuilderGems from './BuilderGems';
import { builderHasCards, builderHasEngravings, builderHasGems, builderHasPaperdoll, builderHasSetBonus, builderHasSkills, builderHasStats, builderIsEmpty } from './common';
import Dialog from 'ui/dialog';
import { getStoredValue, setStoredValue } from 'common';
//import AdUnit from 'AdUnit';

function BuilderToggle({ id, children }) {
  const value = Store.useBuilder(builder => builder[id]);
  const dispatch = Store.useDispatch();
  const onToggle = React.useCallback(() => dispatch.setBuilder(builder => ({...builder, [id]: !builder[id]})), [dispatch, id]);
  if (value) return null;
  return (
    <Tooltip tooltip="Show this block">
      <button className="lap-BuilderToggle" onClick={onToggle}>
        <FontAwesomeIcon icon={faEye}/>
        {children}
      </button>
    </Tooltip>
  );
}

const ClassDisplay = ({ value }) => (
  <div className="lap-class lap-readonly">
    <span className={`lap-class-icon lap-icon-medium lap-class-${value}`}/>
    {_F(Data.playerClasses[value].name)}
  </div>
);

const builderOptions = [
  {id: 'leveling', name: 'Leveling'},
  {id: 'chaos', name: 'Chaos Dungeons'},
  {id: 'raids', name: 'Raids'},
  {id: 'inferno', name: 'Inferno Raids'},
  {id: 'arena', name: 'Arena PvP'},
  {id: 'gvg', name: 'Guild vs. Guild'},
];

const renderCategory = ({id, name}) => <span><span className={`lap-builder-category lap-category-${id}`}/> {name}</span>;

const BuilderCategory = React.memo(function BuilderCategory() {
  const value = Store.useBuilder(builder => builder.category);
  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(opt => dispatch.setBuilder(builder => ({...builder, category: opt?.id})), [dispatch]);
  return (
    <Select options={builderOptions}
            renderOption={renderCategory}
            renderValue={renderCategory}
            value={value && builderOptions.find(opt => opt.id === value)}
            onChange={onChange}
            className="lap-save-category"
            allowEmpty
            placeholder="Select category..."
    />
  );
});

function ClassConfirmation({ onResolve, onClose }) {
  return (
    <Dialog title={`Change class`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{`Changing class will reset all data. Continue?`}</p>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(true)}>{`Yes`}</button>
        <button onClick={() => onResolve(false)}>{`No`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

const localeList = [
  {id: 'en', name: 'English'},
  {id: 'de', name: 'German'},
  {id: 'es', name: 'Spanish'},
  {id: 'fr', name: 'French'},
];
const renderLocale = opt => <Tooltip tooltip={opt.name}><span className={`lap-locale lap-locale-${opt.id}`}/></Tooltip>;

function AutoSave() {
  const imported = useImportId();
  const builder = Store.useBuilder();

  React.useEffect(() => {
    if (!builderIsEmpty(builder)) {
      const handle = setTimeout(() => {
        setStoredValue('lap-stateBackup', imported ? null : builder, true);
      }, 1000);
      return () => clearTimeout(handle);
    }
  }, [builder, imported]);

  return null;
}

function insertAds(list, base) {
  return list;
  // const blocks = [];
  // for (const block of list) {
  //   blocks.push(block);
  //   blocks.push(<AdUnit key={blocks.length} id={`labuilder-column-${Math.ceil(blocks.length / 2) + base}`} className="labuilder-ad-column"/>);
  // }
  // return blocks;
}

const BuilderApp = React.memo(function BuilderApp({ loading }) {
  const overlays = React.useContext(OverlayContext);

  const locale = getStoredValue('lap-language', 'en');
  const setLocale = React.useCallback(opt => {
    setStoredValue('lap-language', opt.id);
    window.location.reload();
  }, []);

  const [spinner, setSpinner] = EditingContext.useState("spinner", 0);
  const [editing] = EditingContext.useState("builderEditing", !loading);
  const [settings, setSettings] = EditingContext.useState("builderSettings", false);
  const openSettings = React.useCallback(() => setSettings(true), [setSettings]);

  const builder = Store.useBuilder();
  const name = Store.useSelector(state => state.name);
  const dispatch = Store.useDispatch();

  const isEmpty = React.useMemo(() => builderIsEmpty(builder), [builder]);

  const fireOnce = React.useRef(false);
  React.useEffect(() => {
    if (fireOnce.current) return;
    fireOnce.current = true;
    if (loading) {
      setSpinner(spin => spin + 1);
      loading.then(error => {
        if (error) {
          overlays.async(<ErrorPopup message={error}/>);
        }
      }).finally(() => setSpinner(spin => spin - 1));
    }
  }, [loading, setSpinner, overlays]);

  React.useEffect(() => {
    if (document.location.search) {
      const url = new URL(document.location.href);
      const reset = url.searchParams && url.searchParams.get("reset");
      if (reset) {
        url.search = "";
        overlays.async(<AccountDialog action="reset" params={{reset_code: reset}}/>);
        window.history.replaceState({}, "", url.href);        
      }
    }
  }, [overlays]);

  const onChangeClass = React.useCallback(async value => {
    if (!isEmpty) {
      if (!await overlays.async(<ClassConfirmation/>)) return;
    }
    dispatch.setBuilder(builder => {
      if (builder.class === value) return builder;
      return {...defaultBuilder, class: value};
    });
  }, [dispatch, isEmpty, overlays]);

  //const points = useBuilderSkillPoints();

  const blocks = [];
  if (builder.category !== 'arena' && builder.category !== 'inferno' && (editing || builderHasPaperdoll(builder))) blocks.push(<BuilderPaperdoll key="paperdoll"/>);
  if (editing || builderHasStats(builder) || builderHasSetBonus(builder)) blocks.push(<BuilderStats key="stats"/>);
  if (builder.category !== 'arena' && (editing || builderHasEngravings(builder))) blocks.push(<BuilderEngravings key="engravings"/>);
  if (editing || builderHasSkills(builder)) blocks.push(<BuilderSkills key="skills"/>);
  if (builder.category !== 'arena' && (editing || builderHasGems(builder))) blocks.push(<BuilderGems key="gems"/>);
  if (builder.category !== 'arena' && builder.category !== 'inferno' && (editing || builderHasCards(builder))) blocks.push(<BuilderCards key="cards"/>);

  return (
    <div className="laplanner-BuilderApp">
      {settings ? (
        <BuilderSettings/>
      ) : <>
        <div className="lap-BuilderHeader">
          {!!editing && <ClassSelect className="lap-class" value={builder.class} onChange={onChangeClass}/>}
          {!editing && <ClassDisplay value={builder.class}/>}
          {!!editing && <BuilderCategory/>}
          {!!editing && (
            <div className="lap-BuilderToggles">
              {/* <BuilderToggle id="showPaperdoll">Items</BuilderToggle>
              <BuilderToggle id="showStats">Stats</BuilderToggle>
              <BuilderToggle id="showEngravings">Engravings</BuilderToggle>
              <BuilderToggle id="showSkills">Skills</BuilderToggle>
              <BuilderToggle id="showGems">Gems</BuilderToggle>
              <BuilderToggle id="showCards">Cards</BuilderToggle> */}
            </div>
          )}
          {!editing && (
            <div className="lap-BuilderTitle">
              {!!(builder.category || name) && <span className="lap-separator"/>}
              {!!builder.category && (
                <Tooltip tooltip={builderOptions.find(opt => opt.id === builder.category)?.name}>
                  <span className={`lap-builder-category lap-category-${builder.category}`}/>
                </Tooltip>
              )}
              {!!name && ` ${name}`}
              {/*!!points && <>
                <span className="lap-separator"/>
                {points} skill points
              </>*/}
            </div>
          )}
          <div className="lap-language">
            <Select value={localeList.find(opt => opt.id === locale)}
              onChange={setLocale}
              options={localeList}
              renderOption={renderLocale}
              renderValue={renderLocale}/>
          </div>
          <div className="lap-toolbar">
            {!!editing && !isEmpty && <ProfileNewButton/>}
            {!!editing && <ProfileUpdateButton builder/>}
            <Tooltip tooltip="Settings">
              <FontAwesomeIcon className="lap-settings" icon={faCog} onClick={openSettings}/>
            </Tooltip>
          </div>
        </div>
        <div className={classNames("lap-MainArea", {"lap-BuilderEditing": editing})}>
          <div className={classNames("lap-block-row lap-row-first", {"lap-row-full": blocks.length >= 4, "lap-row-empty": blocks.length <= 1})}>
            {insertAds(blocks.slice(0, 4), 0)}
          </div>
          {blocks.length > 4 && (
            <div className={classNames("lap-block-row lap-row-second", {"lap-row-empty": blocks.length <= 5, "lap-row-full": blocks.length >= 6})}>
              {insertAds(blocks.slice(4), 4)}
            </div>
          )}
        </div>
        {spinner > 0 && (
          <Overlay>
            <div className="lap-Spinner"/>
          </Overlay>
        )}
      </>}
      <AutoSave/>
    </div>
  );
});

export default function BuilderContext({ session, loading, editingContext }) {
  return (
    <React.Fragment>
      {/* <AdUnit id="labuilder-left" className="labuilder-ad-left"/> */}
      <DndProviderWrapper>
        <Store.Provider store={Store.instance}>
          <Session.Provider store={session}>
            <EditingContext.Provider context={editingContext}>
              <OverlayRoot>
                <ImportTracker builder>
                  <BuilderApp loading={loading}/>
                </ImportTracker>
              </OverlayRoot>
            </EditingContext.Provider>
          </Session.Provider>
        </Store.Provider>
      </DndProviderWrapper>
      {/* <AdUnit id="labuilder-right" className="labuilder-ad-right"/> */}
    </React.Fragment>
  );
}
